// Pure domain logic for the Tinnitogram tab — kept dependency-free so
// both the live editor and the printed report share exactly the same
// derivations and defaults.

export const PITCH_FREQS   = [125, 250, 500, 1000, 2000, 4000, 8000]; // Hz
export const MASKING_LEVELS = [0, 10, 20, 30, 40, 50, 60, 70, 80];     // dB HL
export const PERCEPTION_BANDS = ['Louder', 'No Change', 'Softer', 'Not Heard']; // top → bottom
export const ENV_OPTIONS = ['Sound Treated Booth', 'Free Field', 'Other'];
export const TRANSDUCER_OPTIONS = ['Insert Earphones', 'Supra-aural', 'Bone Conductor'];
export const EAR_OPTIONS = ['Right', 'Left', 'Bilateral'];
export const YES_NO = ['Yes', 'No'];
export const PERCEPTION_NATURES = ['Pulsatile', 'Non-pulsatile, Continuous', 'Non-pulsatile, Intermittent'];
export const RELIABILITY_OPTIONS = ['Good', 'Fair', 'Poor'];

export const PITCH_HL_MIN = -10;
export const PITCH_HL_MAX = 120;
export const LOUDNESS_MAX_DB = 60;
export const MASK_MAX_DB = 80;

export const DEFAULT_PROCEDURE = 'Tinnitus Pitch & Loudness Matching (Tinnitogram)';
export const DEFAULT_PURPOSE   = 'Characterization of tinnitus';
export const DEFAULT_TEST_STANDARD = 'Routine Clinical Protocol';

// Immutable setter helper -------------------------------------------
const cloneWith = (obj, path, value) => {
  const [head, ...rest] = path;
  const next = { ...(obj || {}) };
  if (rest.length === 0) {
    if (value === null || value === undefined || value === '') delete next[head];
    else next[head] = value;
  } else next[head] = cloneWith(next[head] || {}, rest, value);
  return next;
};

export const setThreshold = (data, ear, freqHz, dbHL) =>
  ({ ...data, pitch: cloneWith(data.pitch || {}, [ear, 'thresholds', String(freqHz)], dbHL) });

export const setPitchMatch = (data, ear, freqHz, dbHL) => {
  let next = { ...data };
  next.pitch = cloneWith(next.pitch || {}, [ear, 'match_freq'], freqHz);
  next.pitch = cloneWith(next.pitch || {}, [ear, 'match_hl'], dbHL);
  return next;
};

export const setLoudness = (data, ear, key, value) =>
  ({ ...data, loudness: cloneWith(data.loudness || {}, [ear, key], value) });

export const setMaskingValue = (data, ear, dbHL, perception) =>
  ({ ...data, masking: cloneWith(data.masking || {}, [ear, 'values', String(dbHL)], perception) });

// ── Normalise arbitrary saved data → strict shape ──────────────────
export function normaliseTinnitogram(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    audiometer:  d.audiometer  || '',
    calibration: d.calibration || '',
    environment: ENV_OPTIONS.includes(d.environment) ? d.environment : 'Sound Treated Booth',
    transducer:  TRANSDUCER_OPTIONS.includes(d.transducer) ? d.transducer : 'Insert Earphones',
    test_ear:    EAR_OPTIONS.includes(d.test_ear) ? d.test_ear : 'Right',
    procedure:   d.procedure   || DEFAULT_PROCEDURE,
    purpose:     d.purpose     || DEFAULT_PURPOSE,
    counseling_given: YES_NO.includes(d.counseling_given) ? d.counseling_given : 'Yes',
    remarks:     d.remarks     || '',
    clinician:   d.clinician   || '',
    refer_by:    d.refer_by    || '',
    test_standards: d.test_standards || DEFAULT_TEST_STANDARD,
    pitch: {
      right: {
        thresholds: (d.pitch && d.pitch.right && d.pitch.right.thresholds) ? { ...d.pitch.right.thresholds } : {},
        match_freq: (d.pitch && d.pitch.right && d.pitch.right.match_freq) ?? null,
        match_hl:   (d.pitch && d.pitch.right && d.pitch.right.match_hl)   ?? null,
      },
      left: {
        thresholds: (d.pitch && d.pitch.left && d.pitch.left.thresholds) ? { ...d.pitch.left.thresholds } : {},
        match_freq: (d.pitch && d.pitch.left && d.pitch.left.match_freq) ?? null,
        match_hl:   (d.pitch && d.pitch.left && d.pitch.left.match_hl)   ?? null,
      },
    },
    loudness: {
      right: {
        loudness_db_sl: d.loudness?.right?.loudness_db_sl ?? null,
        mml_db_sl:      d.loudness?.right?.mml_db_sl      ?? null,
        ri_present:     !!d.loudness?.right?.ri_present,
        ri_minutes:     d.loudness?.right?.ri_minutes     ?? null,
      },
      left: {
        loudness_db_sl: d.loudness?.left?.loudness_db_sl ?? null,
        mml_db_sl:      d.loudness?.left?.mml_db_sl      ?? null,
        ri_present:     !!d.loudness?.left?.ri_present,
        ri_minutes:     d.loudness?.left?.ri_minutes     ?? null,
      },
    },
    masking: (() => {
      const m = d.masking && typeof d.masking === 'object' ? d.masking : {};
      const ear = m.ear === 'left' ? 'left' : 'right';
      const hasNew = (m.right && m.right.values) || (m.left && m.left.values);
      if (hasNew) {
        return {
          ear,
          right: { values: m.right && m.right.values ? { ...m.right.values } : {} },
          left:  { values: m.left  && m.left.values  ? { ...m.left.values  } : {} },
        };
      }
      // Legacy shape: { ear, values } → migrate under active ear.
      const legacy = m.values && typeof m.values === 'object' ? { ...m.values } : {};
      return {
        ear,
        right: { values: ear === 'right' ? legacy : {} },
        left:  { values: ear === 'left'  ? legacy : {} },
      };
    })(),
    summary: {
      tinnitus_type:      d.summary?.tinnitus_type      || '',
      perception_nature:  PERCEPTION_NATURES.includes(d.summary?.perception_nature) ? d.summary.perception_nature : 'Non-pulsatile, Continuous',
      test_reliability:   RELIABILITY_OPTIONS.includes(d.summary?.test_reliability) ? d.summary.test_reliability : 'Good',
    },
    clinical_interpretation: d.clinical_interpretation || '',
    recommendations:         d.recommendations || '',
    fields: d.fields || {},
  };
}

// ── Auto-derivations ──────────────────────────────────────────────
export function derivePitchType(freqHz) {
  if (!freqHz || !Number.isFinite(Number(freqHz))) return '';
  const f = Number(freqHz);
  if (f < 500) return 'Low Pitch';
  if (f <= 2000) return 'Mid Pitch';
  return 'High Pitch';
}

export function deriveLaterality(testEar) {
  if (testEar === 'Right')    return 'Right Ear (Unilateral)';
  if (testEar === 'Left')     return 'Left Ear (Unilateral)';
  if (testEar === 'Bilateral') return 'Bilateral';
  return '';
}

export function activeEarForSummary(state) {
  // Prefer whichever ear has a pitch match set; fall back to test_ear.
  if (state.pitch?.right?.match_freq) return 'right';
  if (state.pitch?.left?.match_freq)  return 'left';
  return state.test_ear === 'Left' ? 'left' : 'right';
}

export function buildClinicalInterpretation(state) {
  const ear = activeEarForSummary(state);
  const p = state.pitch?.[ear] || {};
  const l = state.loudness?.[ear] || {};
  const lat = deriveLaterality(state.test_ear).toLowerCase().replace(' (unilateral)', '');
  const type = derivePitchType(p.match_freq);
  const bullets = [];
  if (p.match_freq && l.loudness_db_sl !== null && l.loudness_db_sl !== undefined) {
    bullets.push(`Tinnitus pitch matched at ${p.match_freq.toLocaleString()} Hz with loudness of ${l.loudness_db_sl} dB SL in the ${ear === 'right' ? 'right' : 'left'} ear.`);
  } else if (p.match_freq) {
    bullets.push(`Tinnitus pitch matched at ${p.match_freq.toLocaleString()} Hz in the ${ear === 'right' ? 'right' : 'left'} ear.`);
  }
  if (l.mml_db_sl !== null && l.mml_db_sl !== undefined) {
    bullets.push(`Minimum masking level at ${l.mml_db_sl} dB SL.`);
  }
  if (l.ri_present && l.ri_minutes) {
    bullets.push(`Residual inhibition observed for ${l.ri_minutes} minutes.`);
  } else if (l.ri_present) {
    bullets.push('Residual inhibition present.');
  } else if (p.match_freq) {
    bullets.push('No residual inhibition observed.');
  }
  if (type && lat) {
    bullets.push(`Findings are consistent with subjective ${lat} ${type.toLowerCase()} tinnitus.`);
  }
  return bullets.join('\n');
}

export function buildRecommendations() {
  return [
    'Provide tinnitus counselling and education.',
    'Consider sound therapy / tinnitus retraining strategies.',
    'Monitor periodically for any change in tinnitus characteristics.',
    'Correlate with pure tone audiometry and clinical history.',
  ].join('\n');
}
